import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Star,
  Clock,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { FORMATIONS } from "./data/formations";
import { TypeBadge } from "./components/Presentiel";

// ===============================
// Helper réutilisé depuis App.js
// ===============================
const handleStripeReservation = (formation) => {
  window.location.href = `/paiement/${formation.code}`;
};

const getReserveLabel = () => "Réserver";

// ===============================
// SECTION 3 — Formations les plus demandées
// ===============================
const PopularFormations = () => {
  const populaires = [
    {
      code: "F09",
      badge: { label: "⭐ Formation Phare", color: "bg-red-500" },
    },
    {
      code: "F10",
      badge: { label: "🔥 Très demandée", color: "bg-[#2196F3]" },
    },
    {
      code: "F11",
      badge: { label: "🚀 Nouveau", color: "bg-green-500" },
    },
    {
      code: "F08",
      badge: { label: "✨ Nouveauté 2026", color: "bg-purple-500" },
    },
  ];

  const cards = populaires
    .map((p) => ({ ...p, formation: FORMATIONS.find((f) => f.code === p.code) }))
    .filter((c) => c.formation);

  return (
    <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="popular-formations">
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-10 h-[2px] bg-[#F07B1D]"></div>
            <span className="text-[#F07B1D] font-semibold text-sm uppercase tracking-wider">Catalogue 2026-2027</span>
            <div className="w-10 h-[2px] bg-[#F07B1D]"></div>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#003087] mb-4 leading-tight">
            Formations les plus demandées
          </h2>
          <p className="text-gray-600 text-lg">
            Sélectionnées par nos apprenants et les entreprises partenaires.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map(({ formation, badge }) => (
            <div
              key={formation.id}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#F07B1D]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
              data-testid={`popular-card-${formation.code}`}
            >
              <div className="relative overflow-hidden" style={{ height: "180px" }}>
                <img
                  src={formation.image}
                  alt={formation.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className={`absolute top-3 left-3 ${badge.color} text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-md`}>
                  {badge.label}
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-2 mb-1"><p className="text-[10px] uppercase tracking-wider font-bold text-[#F07B1D]">{formation.domain}</p><TypeBadge type={formation.type} /></div>
                <h3 className="font-heading font-bold text-[#003087] mb-2 leading-tight text-base">
                  {formation.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formation.duration}</span>
                  <span>·</span>
                  <span>{formation.modality}</span>
                </div>
                <div className="mt-auto">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-heading text-2xl font-extrabold text-[#003087]">{formation.price}€</span>
                    {formation.oldPrice && <span className="text-xs text-gray-400 line-through">{formation.oldPrice}€</span>}
                  </div>
                  <Link to={`/formations/${formation.slug}`}>
                    <Button className="w-full bg-[#F07B1D] text-white font-bold text-sm hover:bg-[#D96A0F] transition-all">
                      Voir le détail
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/catalogue">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-[#003087] text-[#003087] font-bold hover:bg-[#003087] hover:text-white transition-all"
              data-testid="see-all-formations"
            >
              Voir toutes les 25 formations
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// ===============================
// SECTION 4 — Pourquoi choisir MGB School
// ===============================
const WhyChoose = () => {
  const cards = [
    {
      emoji: "🎯",
      title: "Pédagogie 100% pratique",
      text: "Nos formations s'appuient sur des cas réels d'entreprise. Vous repartez avec des outils applicables dès le lendemain. Pas de théorie creuse, que du concret.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80",
    },
    {
      emoji: "👨‍🏫",
      title: "Formateurs professionnels",
      text: "Nos intervenants sont des praticiens issus du monde de l'entreprise, du droit et du digital — pas uniquement des enseignants théoriques.",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80",
    },
    {
      emoji: "♿",
      title: "Accessible à tous",
      text: "Présentiel Paris 8e ou 100% distanciel selon votre situation. Référent handicap disponible. Chaque situation est étudiée individuellement.",
      image: "https://images.unsplash.com/photo-1587614382346-4ec70e388b28?w=600&q=80",
    },
    {
      emoji: "💳",
      title: "Organisme déclaré",
      text: "MGB School est un organisme de formation déclaré (NDA 11757727775). Financement individuel ou plan de formation entreprise ; les démarches OPCO, France Travail et CPF sont en cours.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="why-choose">
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#003087] mb-4 leading-tight">
            Pourquoi choisir MGB School ?
          </h2>
          <p className="text-gray-600 text-lg">
            Une pédagogie orientée compétences, conçue pour des résultats concrets.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c) => (
            <div
              key={c.title}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#F07B1D]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
              data-testid={`why-card-${c.title}`}
            >
              <div className="relative overflow-hidden" style={{ height: "200px" }}>
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3 w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center text-2xl">
                  {c.emoji}
                </div>
              </div>
              <div className="p-6 flex-1">
                <h3 className="font-heading font-bold text-[#003087] mb-2 text-lg leading-tight">{c.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===============================
// SECTION 5 — Témoignages
// ===============================
const Testimonials = () => {
  return (
    <section className="py-10 md:py-12 bg-[#1565C0] relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute -right-32 -top-32 w-96 h-96 bg-[#F07B1D]/10 rounded-full blur-3xl"></div>
      <div className="container-custom relative">
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 bg-[#1976d2] border border-white/15 rounded-2xl px-6 py-5 md:px-8"
          style={{ borderLeft: "4px solid #F07B1D" }}
          data-testid="testimonial-placeholder-1"
        >
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-1 leading-tight">
              Vous avez suivi une formation MGB School ?
            </h2>
            <p className="text-white/75 text-sm">
              Les premiers témoignages de nos apprenants 2026-2027 seront publiés ici. Partagez votre expérience en quelques lignes.
            </p>
          </div>
          <a
            href="mailto:contact@mgbschool.fr?subject=Mon%20avis%20sur%20MGB%20School"
            className="inline-block shrink-0 bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold px-6 py-3 rounded-lg text-sm transition-colors"
            data-testid="testimonials-leave-review-btn"
          >
            Laisser un avis →
          </a>
        </div>
      </div>
    </section>
  );
};

// ===============================
// SECTION 6 — Positionnement Financeurs
// ===============================
const Financeurs = () => {
  const blocs = [
    {
      emoji: "👤",
      title: "Financement individuel",
      text: "Inscription directe et paiement sécurisé en ligne. Contrat de formation professionnelle, délai de rétractation de 10 jours, certificat de réalisation et facture conforme fournis.",
      link: "Voir les formations",
      to: "/catalogue",
    },
    {
      emoji: "🏢",
      title: "Plan de formation entreprise",
      text: "TPE, PME et grandes entreprises peuvent intégrer nos formations dans leur plan de développement des compétences. Convention de formation et devis personnalisé sur demande.",
      link: "Demander un devis",
      to: "/contact",
    },
    {
      emoji: "🏦",
      title: "OPCO — démarches en cours",
      text: "Organisme déclaré (NDA 11757727775), MGB School constitue actuellement les dossiers de référencement auprès des OPCO. Nous vous informons dès l'ouverture des prises en charge.",
      link: "Être informé",
      to: "/contact",
    },
    {
      emoji: "🎯",
      title: "France Travail & CPF — démarches en cours",
      text: "Les démarches de référencement France Travail et d'éligibilité CPF sont engagées. Nous mettrons cette page à jour dès l'obtention des éligibilités.",
      link: "Parler à un conseiller",
      to: "/contact",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="financeurs">
      <div className="container-custom">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#003087] mb-4 leading-tight">
            Financement de votre formation
          </h2>
          <p className="text-gray-600 text-lg">
            MGB School est un <strong>organisme de formation déclaré</strong> (NDA 11757727775). Nos formations sont accessibles en financement individuel ou via le plan de formation de votre entreprise.
          </p>
        </div>

        <div
          className="max-w-3xl mx-auto mb-10 p-4 md:p-5 rounded-xl flex items-start gap-3"
          style={{ backgroundColor: "#FFF4E6", border: "1px solid #F07B1D" }}
          data-testid="honesty-banner-financement"
        >
          <span className="text-2xl shrink-0">📋</span>
          <p className="text-sm md:text-[15px] text-[#7A3B00] leading-relaxed">
            <strong>Transparence :</strong> les prises en charge OPCO, France Travail et CPF ne sont pas encore ouvertes ; les démarches de référencement sont en cours. Cette page sera mise à jour dès l'obtention des éligibilités.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {blocs.map((b) => (
            <div
              key={b.title}
              className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-[#F07B1D]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col"
              style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
              data-testid={`financeur-${b.title}`}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#F07B1D]/10 flex items-center justify-center text-4xl mb-5">
                {b.emoji}
              </div>
              <h3 className="font-heading font-bold text-[#003087] mb-3 text-lg leading-tight">{b.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">{b.text}</p>
              <Link
                to={b.to}
                className="text-[#F07B1D] hover:text-[#D96A0F] text-sm font-bold inline-flex items-center gap-1 transition-colors"
              >
                {b.link}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===============================
// HERO PRINCIPAL (Section 2)
// ===============================
const Hero = () => {
  return (
    <section
      className="relative py-12 md:py-20 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #1565C0 0%, #2196F3 100%)" }}
      data-testid="hero-section"
    >
      <div className="absolute top-0 right-0 w-2 h-full bg-[#F07B1D] hidden md:block"></div>
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Texte gauche */}
          <div>
            <Badge className="bg-[#F07B1D] text-white font-semibold mb-6 px-3 py-1.5 text-xs uppercase tracking-wider">
              🎓 Organisme de formation · Paris 8e
            </Badge>
            <h1
              className="font-heading font-extrabold text-white mb-6 leading-[1.1]"
              style={{ fontSize: "clamp(28px, 5vw, 40px)" }}
            >
              Des formations courtes et efficaces pour <span className="text-[#F07B1D]">booster votre carrière</span> et votre entreprise
            </h1>
            <p className="text-white/85 text-base md:text-lg mb-8 leading-relaxed">
              MGB School vous accompagne dans votre montée en compétences : gestion, droit, digital et intelligence artificielle. Formations finançables, accessibles, certifiantes.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/catalogue">
                <Button
                  size="lg"
                  className="bg-[#F07B1D] text-white font-bold hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-lg shadow-[#F07B1D]/30 w-full sm:w-auto"
                  data-testid="hero-cta-catalogue"
                >
                  Voir nos formations
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white font-bold hover:bg-white hover:text-[#003087] transition-all bg-transparent w-full sm:w-auto"
                  data-testid="hero-cta-contact"
                >
                  Nous contacter
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2 text-white/80 text-xs md:text-sm">
              {[
                "Organisme déclaré · NDA 11757727775",
                "Financement individuel ou entreprise",
                "Accès distanciel",
                "Suivi personnalisé",
              ].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-[#F07B1D]" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Image droite — vraie salle MGB School */}
          <div className="relative hidden lg:block">
            <div
              className="rounded-2xl overflow-hidden"
              style={{ borderRadius: "16px", boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }}
            >
              <img
                src="/salle-formation-mgb.jpg"
                alt="Espace de formation MGB School"
                loading="eager"
                className="w-full h-auto object-cover"
                style={{ aspectRatio: "1 / 1" }}
              />
            </div>
          </div>
        </div>

        {/* 3 cartes KPI en bas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
          {[
            { title: "25 formations", sub: "Catalogue 2026-2027" },
            { title: "Organisme déclaré", sub: "NDA 11757727775 · Île-de-France" },
            { title: "Accès", sub: "Présentiel Paris 8e + Distanciel" },
          ].map((k) => (
            <div
              key={k.title}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-4"
              data-testid={`hero-kpi-${k.title}`}
            >
              <p className="font-heading font-bold text-white text-lg leading-tight">{k.title}</p>
              <p className="text-white/70 text-xs mt-1">{k.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===============================
// HOMEPAGE — assemblage des 6 sections
// ===============================
export const HomePage = ({ BandeauConfiance, BandeauFinanceurs }) => {
  return (
    <div className="pt-20 md:pt-24" data-testid="home-page">
      <BandeauConfiance />
      <Hero />
      <BandeauFinanceurs />
      <PopularFormations />
      <WhyChoose />
      <Testimonials />
      <Financeurs />
    </div>
  );
};

export default HomePage;
